import { useState, useRef } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { useImageUpload } from "@/hooks/useImageUpload";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { X, Image as ImageIcon, Camera } from "lucide-react";
import { toast } from "sonner";
import ImageComponent from "@/components/ui/image";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CameraDialog } from "@/components/images/camera-dialog";

export type EntityType = "product" | "company" | "user" | "generic";

interface ImageUploadProps {
  entityType: EntityType;
  entityId?: Id<"products" | "companies" | "users">;
  currentImageId?: Id<"_storage"> | undefined;
  onImageChange?: (imageId: Id<"_storage"> | undefined) => void;
  disabled?: boolean;
  disableCamera?: boolean;
  maxWidth?: number;
  maxHeight?: number;
  maxSizeKB?: number;
  aspectRatio?: number;
  quality?: number;
  uploadText?: string;
  dragText?: string;
  selectText?: string;
  className?: string;
}

export default function ImageUpload({
  entityType,
  entityId,
  currentImageId,
  onImageChange,
  disabled = false,
  disableCamera = false,
  maxWidth = 600,
  maxHeight = 600,
  maxSizeKB = 200,
  aspectRatio = 1,
  quality = 0.8,
  uploadText = "Subir imagen",
  dragText = "Suelta la imagen aquí",
  selectText = "Seleccionar imagen",
  className = "",
}: ImageUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);

  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const getImageDimensions = (
    file: File,
  ): Promise<{ width: number; height: number }> => {
    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
        });
        URL.revokeObjectURL(objectUrl);
      };

      img.src = objectUrl;
    });
  };

  const { uploadImage, isUploading, uploadProgress } = useImageUpload({
    maxWidth,
    maxHeight,
    quality,
    maxSizeKB,
    aspectRatio: imageDimensions
      ? imageDimensions.width / imageDimensions.height
      : aspectRatio,
  });

  // Mutations para diferentes tipos de entidades
  const updateProductImage = useMutation(
    api.companies.configuration.images.mutations.updateProductImage,
  );
  const updateCompany = useMutation(api.companies.mutations.updateCompany);
  const removeCompanyLogo = useMutation(
    api.companies.mutations.removeCompanyLogo,
  );
  const updateMyUserProfilePhoto = useMutation(
    api.system.users.mutations.updateMyUserProfilePhoto,
  );

  const currentImageUrl = useQuery(
    api.companies.configuration.images.queries.getImageUrl,
    currentImageId ? { storageId: currentImageId } : "skip",
  );

  const handleFileSelect = async (file: File) => {
    if (disabled || !file) return;

    // Verificar que sea una imagen
    if (!file.type.startsWith("image/")) {
      toast.error("Por favor selecciona un archivo de imagen válido");
      return;
    }

    // Limpiar dimensiones anteriores
    setImageDimensions(null);

    // Procesar la imagen
    await handleUpload(file);
  };

  const handleUpload = async (file: File) => {
    try {
      // Obtener dimensiones reales de la imagen
      const dimensions = await getImageDimensions(file);
      setImageDimensions(dimensions);

      // Subir la imagen con las dimensiones calculadas
      const storageId = await uploadImage(file);
      if (!storageId) return;

      // Notificar al componente padre
      onImageChange?.(storageId as Id<"_storage">);

      // Actualizar en la base de datos según el tipo de entidad
      if (entityId) {
        if (entityType === "product") {
          await updateProductImage({
            productId: entityId as Id<"products">,
            imageId: storageId as Id<"_storage">,
          });
        } else if (entityType === "company") {
          await updateCompany({
            id: entityId as Id<"companies">,
            logoId: storageId as Id<"_storage">,
          });
        } else if (entityType === "user") {
          await updateMyUserProfilePhoto({
            image: storageId as Id<"_storage">,
          });
        }
        // Agregar más tipos según necesites
      }

      toast.success("Imagen subida exitosamente");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Error al subir la imagen");
    }
  };

  const handleRemoveImage = async () => {
    if (disabled) return;

    try {
      // Limpiar el estado de dimensiones
      setImageDimensions(null);

      // Notificar al componente padre
      onImageChange?.(undefined);

      // Actualizar en la base de datos según el tipo de entidad
      if (entityId) {
        if (entityType === "product") {
          await updateProductImage({
            productId: entityId as Id<"products">,
            imageId: undefined,
          });
        } else if (entityType === "company") {
          await removeCompanyLogo({
            id: entityId as Id<"companies">,
          });
        } else if (entityType === "user") {
          await updateMyUserProfilePhoto({
            image: undefined,
          });
        }
        // Agregar más tipos según necesites
      }

      // Limpiar el input de archivo para permitir volver a cargar la misma imagen
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      toast.success("Imagen eliminada");
    } catch (error) {
      console.error("Error removing image:", error);
      toast.error("Error al eliminar la imagen");
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find((file) => file.type.startsWith("image/"));

    if (imageFile) {
      handleFileSelect(imageFile);
    } else {
      toast.error("Por favor arrastra un archivo de imagen válido");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const getEntityText = () => {
    switch (entityType) {
      case "product":
        return "producto";
      case "company":
        return "empresa";
      case "user":
        return "usuario";
      case "generic":
        return "elemento";
      default:
        return "elemento";
    }
  };

  const getSizeText = (file?: File) => {
    if (file) {
      const sizeInKB = Math.round(file.size / 1024);
      const sizeText =
        sizeInKB > 1024
          ? `${(sizeInKB / 1024).toFixed(1)} MB`
          : `${sizeInKB} KB`;
      return `${sizeText} • ${maxSizeKB} KB máx.`;
    }
    return `Máximo ${maxSizeKB} KB`;
  };

  return (
    <div className={className}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Card
            className={`relative transition-colors w-full p-0 overflow-hidden ${
              currentImageUrl
                ? "border-0 shadow-none"
                : dragOver
                  ? "border-blue-500 bg-blue-50"
                  : "border-dashed border-muted-foreground/30"
            } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            style={{ aspectRatio: String(aspectRatio) }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !disabled && fileInputRef.current?.click()}
          >
            <CardContent className="relative w-full h-full overflow-hidden rounded p-0">
              {currentImageUrl ? (
                <ImageComponent
                  src={currentImageUrl}
                  alt={`Imagen de ${getEntityText()}`}
                  fill
                  className="object-cover"
                />
              ) : currentImageId && currentImageUrl === undefined ? (
                <Skeleton className="absolute inset-0 rounded-none" />
              ) : currentImageId && currentImageUrl === null ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-muted-foreground">
                  <ImageIcon className="h-8 w-8 opacity-60" />
                  <p className="text-xs text-center px-3">
                    Imagen no disponible
                  </p>
                  <p className="text-[10px] text-center px-3 opacity-70">
                    Sube una nueva para reemplazar el registro huerfano.
                  </p>
                </div>
              ) : isUploading ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center space-y-4 text-gray-500">
                  <Spinner className="h-8 w-8" />
                  <div className="w-full max-w-xs px-4">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                  <p className="text-sm">
                    Subiendo imagen... {uploadProgress}%
                  </p>
                </div>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                  <ImageIcon className="h-8 w-8" />
                </div>
              )}
              {currentImageUrl && !disabled && (
                <Button
                  type="button"
                  variant="destructive"
                  size="icon-sm"
                  className="absolute top-2 right-2 p-2 "
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage();
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </CardContent>
          </Card>
        </TooltipTrigger>
        {!currentImageUrl && !isUploading && !currentImageId && (
          <TooltipContent side="bottom" className="text-center">
            <p className="font-medium">{dragOver ? dragText : uploadText}</p>
            <p className="text-slate-400">Clic para seleccionar</p>
            <p className="text-slate-400">{getSizeText()}</p>
          </TooltipContent>
        )}
      </Tooltip>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
      />

      {!currentImageUrl && !isUploading && !currentImageId && !disableCamera && (
        <div className="flex gap-2 mt-2 justify-center">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => setCameraOpen(true)}
            disabled={disabled}
            className="h-8 w-8"
          >
            <Camera className="h-4 w-4" />
          </Button>
        </div>
      )}

      <CameraDialog
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={(file) => {
          setCameraOpen(false);
          handleFileSelect(file);
        }}
      />
    </div>
  );
}
